import { Injectable } from '@angular/core';

import { QuickGuideVisibilityService } from '../../../shared/quick-guide/quick-guide-visibility.service';
import { TourConfigService } from './tours/tour-config.service';
import { FeatureTourStep, FeatureTourStepContent, TourScenarioRecord, TourScenarioSummary } from './tours/tour.types';

export interface TourTableOfContentsItem {
  id: string;
  feature: string;
  title: string;
  sections: string[];
  totalSteps: number;
}

function duplicateStep(step: FeatureTourStep): FeatureTourStep {
  return {
    ...step,
    offset: step?.offset ? { ...step.offset } : step?.offset,
    media: step?.media ? { ...step.media } : step?.media,
    backdropConfig: step?.backdropConfig ? { ...step.backdropConfig } : step?.backdropConfig,
    stepDimensions: step?.stepDimensions ? { ...step.stepDimensions } : step?.stepDimensions,
    placement: step?.placement ? { ...step.placement } : step?.placement,
    route: Array.isArray(step.route) ? [...step.route] : step.route,
    keywords: step?.keywords ? [...step.keywords] : step?.keywords,
    richContent: cloneRichContent(step.richContent),
  };
}

function cloneRichContent(content: FeatureTourStepContent | undefined): FeatureTourStepContent | undefined {
  if (!content) {
    return undefined;
  }

  return {
    shortDescription: content.shortDescription,
    longDescription: content.longDescription,
    impactOnSystem: content.impactOnSystem,
    risks: content.risks,
  };
}

@Injectable({ providedIn: 'root' })
export class TourScenarioService {
  private readonly scenarios: TourScenarioRecord[];
  private readonly summaries: TourScenarioSummary[];
  private readonly json: string;

  constructor(
    private readonly tourConfigService: TourConfigService,
    private readonly quickGuideVisibilityService: QuickGuideVisibilityService,
  ) {
    if (!this.quickGuideVisibilityService.isEnabled()) {
      this.scenarios = [];
      this.summaries = [];
      this.json = '[]';
      this.publishGlobalScenarios();
      return;
    }

    this.scenarios = this.tourConfigService.getScenarioRecords();
    this.summaries = this.tourConfigService.getScenarioSummaries();
    this.json = this.tourConfigService.getScenariosJson();

    this.publishGlobalScenarios();
  }

  private publishGlobalScenarios(): void {
    if (typeof window !== 'undefined') {
      try {
        (window as unknown as Record<string, unknown>).__APP_TOUR_SCENARIOS__ = JSON.parse(this.json);
      } catch {
        (window as unknown as Record<string, unknown>).__APP_TOUR_SCENARIOS__ = [];
      }
      (window as unknown as Record<string, unknown>).__APP_TOUR_SCENARIOS_JSON__ = this.json;
    }
  }

  getAll(): TourScenarioRecord[] {
    return this.scenarios.map((scenario) => ({
      ...scenario,
      steps: scenario.steps.map((step) => duplicateStep(step)),
    }));
  }

  getSummary(): TourScenarioSummary[] {
    return this.summaries.map((summary) => ({
      ...summary,
      sections: [...summary.sections],
      tags: [...summary.tags],
      keywords: [...summary.keywords],
    }));
  }

  getTableOfContents(): TourTableOfContentsItem[] {
    return this.summaries.map((summary) => ({
      id: summary.id,
      feature: summary.feature,
      title: summary.title,
      sections: [...summary.sections],
      totalSteps: summary.totalSteps,
    }));
  }

  getScenario(id: string): TourScenarioRecord | undefined {
    const scenario = this.tourConfigService.getScenarioById(id);

    if (!scenario) {
      return undefined;
    }

    return {
      ...scenario,
      steps: scenario.steps.map((step) => duplicateStep(step)),
    };
  }

  getJson(): string {
    return this.json;
  }

  getJsonObject(): unknown {
    return JSON.parse(this.json);
  }
}
